import type { PureFunction } from '@bonsai-components/utility-types';
import type { CameraControls } from '@react-three/drei';
import type { useRouter } from 'next/router';
import React from 'react';
import type * as THREE from 'three';
import type { NonFunctionProperties } from '../@types';
import type {
  BuilderRecipeDetailsFragment,
  RecipeDetailsFragment
} from '../__generated__/apollo-hooks';
import { BUILDER_DEFAULT_RIGHT_SIDEBAR_WIDTH } from '../constants/builder';
import { promptForRecipeOverride } from '../helpers/builder/builder-dialog.helpers';
import { normalizeRecipeGraph } from '../helpers/builder/builder-graph-normalize.helpers';
import {
  cleanLinks,
  findDescendantNodeIds
} from '../helpers/builder/builder-links.helpers';
import { removeNode } from '../helpers/builder/builder-nodes.helpers';
import {
  addRecipeDetailsFragmentToNode,
  makeParentsCustomRecipe,
  stripLegacyBuilderFields,
  updateCurrentRecipeDetails,
  updateSelectedNodeOptions
} from '../helpers/builder/builder-recipe.helpers';
import {
  type CreateYamlOptions,
  createYamlFromNodesAndLinks
} from '../helpers/builder/builder-yaml.helpers';
import {
  type BuilderRecipe,
  db,
  type GraphLink,
  type GraphNodeData
} from '../helpers/indexedDb.helper';
import {
  safeLocalStorage,
  safeSessionStorage
} from '../helpers/local-storage.helper';
import { createTemporalStore } from './store-creators';

const CUSTOM_RECIPE_ID_STORAGE_KEY = 'customRecipeId';

export const ROOT_NODE_ID = 0;

export const TITLE_FONT_SIZE = [10, 10, 10, 12];

const nodePositions = React.createRef<
  Record<number, THREE.Vector3>
>() as React.MutableRefObject<Record<number, THREE.Vector3>>;
nodePositions.current = {};

const cameraControlsRef =
  React.createRef<CameraControls>() as React.MutableRefObject<CameraControls>;

/**
 * Wrap a function so that the loading state is set before the call and reset after
 */
const withLoading = <T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T
): T => {
  return (async (...args: Parameters<T>) => {
    const setIsLoading = (isLoading: boolean) => {
      const currentLoading = useBuilderStore.getState().builderLoading;
      if (currentLoading !== isLoading) {
        useBuilderStore.getState().update({ builderLoading: isLoading });
      }
    };
    setIsLoading(true);
    try {
      return await fn(...args);
    } finally {
      setIsLoading(false);
    }
  }) as unknown as T;
};

type BuilderDialogStates = {
  /** For deleting an entire recipe */
  showDeleteCustomRecipeDialog: boolean;
  /** For deleting a recipe node */
  showDeleteRecipeDialog: boolean;
  showEditCurrentRecipeAsYamlDialog: boolean;
  showEditNestedRecipeDialog: boolean;
  showEditRecipeDialog: boolean;
  showEditRecipeOptionsDialog: boolean;
  showEditYamlDialog: boolean;
  showManageCustomRecipePanel: boolean;
  showNewRecipeDialog: boolean;
  showOverrideRecipeDialog: boolean;
  showPreconditionFinderPanel: boolean;
  showRecipeFinderPanel: boolean;
};

type BuilderUIStates = {
  builderLoading: boolean;
  builderSaving: boolean;
  /** Enables being able to use orbital controls from outside of the Canvas */
  cameraControlsRef: typeof cameraControlsRef;
  customCameraFunctions: {
    zoomToNode: (id: number) => void;
    zoomToFit: () => void;
  };
  debug: boolean;
  /** Auto-rotation state for topology visualization */
  isRotationPlaying: boolean;
  lastInteractionTime: number;
  nodePositions: typeof nodePositions;
  rightSideBarWidth: string;
  searchTerm: string;
  showLegend: boolean;
  showSelectedNodeFloatingPane: boolean;
  treeExpandedItems: string[];
  router: ReturnType<typeof useRouter>;
};

type BuilderRecipeState = {
  currentRecipe: BuilderRecipe;
  currentRecipeDirtyState: BuilderRecipe;
  customRecipes: BuilderRecipe[];
  deletePendingCustomRecipeIds: string[];
  lastDeleteCount?: number;
  descendantNodeIds: Set<number>;
  isReplay: boolean;
  nodeMap: Map<number, GraphNodeData>;
  preconditionCount: number;
  recipeCount: number;
  recipeId: string;
  selectedRecipeNode: GraphNodeData;
};

type BuilderActionsState = {
  addRecipeToNode: (
    recipe: RecipeDetailsFragment | BuilderRecipeDetailsFragment,
    nodeId: number,
    isPrecondition?: boolean,
    selectAfter?: boolean,
    addToTop?: boolean,
    isMissingRecipe?: boolean
  ) => Promise<number>;
  createNewRecipe: (
    name: string,
    id: string,
    description?: string
  ) => BuilderRecipe;
  currentRecipeToYaml: (createYamlOptions?: CreateYamlOptions) => string;
  deleteCustomRecipes: (ids: string[]) => Promise<void>;
  fetchCustomRecipes: () => Promise<void>;
  initialize: (router: ReturnType<typeof useRouter>) => Promise<void>;
  loadCustomRecipe: (id: string) => Promise<void>;
  loadLastCheckpoint: () => Promise<void>;
  loadRecipeFromData: (
    name: string,
    id: string,
    nodes: GraphNodeData[],
    links: GraphLink[],
    forceSave?: boolean
  ) => Promise<void>;
  loadRecipeFromDataTemporarily: (
    name: string,
    id: string,
    nodes: GraphNodeData[],
    links: GraphLink[]
  ) => Promise<void>;
  removeRecipeNode: (nodeId: number) => Promise<void>;
  reset: () => void;
  resetTreeExpandedItems: () => void;
  saveCustomRecipe: PureFunction<
    {
      recipe: BuilderRecipe;
      forceSave?: boolean;
      createCheckpoint?: boolean;
      autoNavigate?: boolean;
    },
    Promise<void>
  >;
  startNewRecipe: () => Promise<void>;
  treeCollapseAll: () => void;
  treeExpandAll: () => void;
  updateCurrentRecipeDetails: (
    name: string,
    id: string,
    description: string,
    nodeId?: number
  ) => Promise<void>;
  updateRootRecipe: (
    nodes: GraphNodeData[],
    links: GraphLink[],
    selectedNode?: number
  ) => Promise<void>;
  updateSelectedRecipe: (selectedRecipeNodeId: number) => void;
  updateSelectedRecipeOptions: (
    options: RecipeDetailsFragment['options']
  ) => void;
  toggleNodePreconditionState: (nodeId: number) => Promise<void>;
};

type BuilderState = BuilderDialogStates &
  BuilderUIStates &
  BuilderRecipeState &
  BuilderActionsState;

const builderDialogDefaults: BuilderDialogStates = {
  showDeleteCustomRecipeDialog: false,
  showDeleteRecipeDialog: false,
  showEditCurrentRecipeAsYamlDialog: false,
  showEditNestedRecipeDialog: false,
  showEditRecipeDialog: false,
  showEditRecipeOptionsDialog: false,
  showEditYamlDialog: false,
  showManageCustomRecipePanel: false,
  showNewRecipeDialog: false,
  showOverrideRecipeDialog: false,
  showPreconditionFinderPanel: false,
  showRecipeFinderPanel: false
};

const builderDialogKeys = Object.keys(
  builderDialogDefaults
) as (keyof BuilderDialogStates)[];

export const selectIsAnyDialogOpen = (state: BuilderState): boolean =>
  builderDialogKeys.some((key) => state[key]);

const builderUIStateDefaults: BuilderUIStates = {
  builderLoading: false,
  builderSaving: false,
  cameraControlsRef,
  customCameraFunctions: null,
  debug: false,
  isRotationPlaying: true,
  lastInteractionTime: Date.now(),
  nodePositions,
  rightSideBarWidth: BUILDER_DEFAULT_RIGHT_SIDEBAR_WIDTH,
  searchTerm: '',
  showLegend: false,
  showSelectedNodeFloatingPane: true,
  treeExpandedItems: ['0'],
  router: null
};

const builderRecipesStateDefaults: BuilderRecipeState = {
  currentRecipe: null,
  currentRecipeDirtyState: null,
  customRecipes: [],
  deletePendingCustomRecipeIds: null,
  descendantNodeIds: new Set<number>(),
  isReplay: false,
  nodeMap: new Map(),
  preconditionCount: 0,
  recipeCount: 0,
  recipeId: null,
  selectedRecipeNode: null
};

const builderStateDefaults: NonFunctionProperties<BuilderState> = {
  ...builderUIStateDefaults,
  ...builderRecipesStateDefaults,
  ...builderDialogDefaults
};

export const useBuilderStore = createTemporalStore<BuilderState>(
  (set, get, store) => {
    store.temporal.getState().clear();
    store.temporal.getState().pause();

    const setWithCheckpoint = (
      currentState: BuilderState | Partial<BuilderState>
    ) => {
      store.temporal.getState().resume();
      set((previousState) => {
        const newState = {
          ...previousState,
          ...builderDialogDefaults,
          ...currentState
        };
        return newState;
      });
      store.temporal.getState().pause();
    };

    return {
      ...builderStateDefaults,
      initialize: withLoading(async (router) => {
        set((state) => ({ ...state, router }));
        await get().fetchCustomRecipes();
        const customRecipes = get().customRecipes;

        if (get().currentRecipe?.id || customRecipes.length === 0) {
          return;
        }

        const idFromSessionStorage = safeSessionStorage.getItem(
          CUSTOM_RECIPE_ID_STORAGE_KEY
        );
        const idFromLocalStorage = safeLocalStorage.getItem(
          CUSTOM_RECIPE_ID_STORAGE_KEY
        );
        const id = idFromSessionStorage || idFromLocalStorage;

        if (id) {
          return get().loadCustomRecipe(id);
        }

        return get().loadCustomRecipe(customRecipes?.at(0)?.id);
      }),
      loadLastCheckpoint: withLoading(async () => {
        store.temporal.getState().undo();
        const historicalState = get().currentRecipe;
        await get().saveCustomRecipe({
          recipe: historicalState,
          createCheckpoint: false
        });
      }),
      addRecipeToNode: withLoading(
        async (
          recipe,
          nodeId,
          isPrecondition,
          selectAfter = true,
          addToTop = false,
          isMissingRecipe = false
        ) => {
          const { newRecipeState, nextAvailableId } =
            addRecipeDetailsFragmentToNode(
              get().currentRecipe,
              recipe,
              nodeId,
              isPrecondition,
              addToTop,
              isMissingRecipe
            );

          await get().saveCustomRecipe({
            recipe: makeParentsCustomRecipe(nextAvailableId, newRecipeState)
          });

          if (selectAfter) {
            get().updateSelectedRecipe(nextAvailableId);
          }

          return nextAvailableId;
        }
      ),
      currentRecipeToYaml: (createYamlOptions = {}) => {
        const currentRecipe = get().currentRecipe;
        if (!currentRecipe) {
          return '';
        }
        const { nodes, links } = currentRecipe;
        return createYamlFromNodesAndLinks(nodes, links, createYamlOptions);
      },
      deleteCustomRecipes: async (ids: string[]) => {
        if (!Array.isArray(ids)) {
          return;
        }

        let shouldClearCurrentRecipe = false;

        // Batch deletions to avoid multiple state updates
        for (const id of ids) {
          const isInDb = await db?.builderRecipes?.get(id);
          if (isInDb) {
            await db?.builderRecipes?.delete(id);
          }
          if (get().currentRecipe?.id === id) {
            shouldClearCurrentRecipe = true;
          }
        }

        set((state) => ({
          ...state,
          ...(shouldClearCurrentRecipe && { currentRecipe: null }),
          lastDeleteCount: ids?.length || 0,
          deletePendingCustomRecipeIds: null
        }));

        await get().fetchCustomRecipes();
      },
      fetchCustomRecipes: async () => {
        const currentRecipeDirtyState = get().currentRecipeDirtyState;
        const customRecipes = (await db?.builderRecipes?.toArray()) || [];
        set((state) => ({ ...state, customRecipes, currentRecipeDirtyState }));
      },
      loadCustomRecipe: withLoading(async (id: string) => {
        if (!id) {
          return;
        }

        if (id !== get().currentRecipe?.id) {
          store.temporal.getState().clear();
        }

        const stored = await db?.builderRecipes?.get(id);
        // update localStorage and sessionStorage with the recipe id
        safeSessionStorage.setItem(CUSTOM_RECIPE_ID_STORAGE_KEY, id);
        safeLocalStorage.setItem(CUSTOM_RECIPE_ID_STORAGE_KEY, id);
        const recipe = stripLegacyBuilderFields(stored);
        const newRecipe = { ...recipe, links: cleanLinks(recipe?.links) };
        const nodeMap = new Map<number, GraphNodeData>(
          newRecipe?.nodes?.map((node) => [node.id, node]) || []
        );

        set((state) => ({
          ...state,
          currentRecipe: newRecipe,
          nodeMap
        }));
      }),
      loadRecipeFromDataTemporarily: withLoading(
        async (name, id, nodes, links) => {
          const nodeMap = new Map<number, GraphNodeData>(
            nodes?.map((node) => [node.id, node]) || []
          );
          set((state) => ({
            ...state,
            currentRecipe: {
              name,
              id,
              description: nodes.find((n) => n.id === ROOT_NODE_ID)?.val
                ?.description,
              nodes,
              links
            },
            nodeMap
          }));
          get().updateSelectedRecipe(ROOT_NODE_ID);
        }
      ),
      loadRecipeFromData: withLoading(
        async (name, id, nodes, links, forceSave = false) => {
          const newRecipe = {
            name,
            id,
            nodes,
            links
          };
          await get().saveCustomRecipe({ recipe: newRecipe, forceSave });
          get().updateSelectedRecipe(ROOT_NODE_ID);
        }
      ),
      createNewRecipe: (name, id, description) => {
        return {
          name,
          id,
          description,
          nodes: [
            {
              isCustomRecipe: true,
              isPrecondition: false,
              id: ROOT_NODE_ID,
              name,
              val: {
                __typename: 'RecipeDescriptor' as const,
                name,
                id,
                instanceName: null,
                description: description ?? '',
                sourceUrl: null,
                recipeCount: 0,
                options: [],
                dataTables: [],
                bundle: {
                  __typename: 'YamlRecipeBundle' as const,
                  requestedVersion: '',
                  version: ''
                },
                detail: {
                  __typename: 'RecipeDetailFinished' as const,
                  startedAt: new Date(),
                  finishedAt: new Date(),
                  recipeList: {
                    __typename: 'RecipeDescriptorConnection' as const,
                    edges: []
                  },
                  tags: [],
                  preconditions: []
                }
              }
            }
          ],
          links: []
        };
      },
      startNewRecipe: async () => {
        set({ showNewRecipeDialog: true });
      },
      removeRecipeNode: withLoading(removeNode),
      reset: () => {
        const {
          currentRecipe: _, // omit will cause infinite rerender loop
          customRecipes: __, // omit after reading from db these don't change unless we are intentional
          builderLoading: ___, // omit, we don't want reset controlling this
          builderSaving: ____, // omit, we don't want reset controlling this
          router: _____,
          ...resetables
        } = builderStateDefaults;
        set((state) => ({ ...state, ...resetables }));
      },
      resetTreeExpandedItems: () => {
        set({ treeExpandedItems: ['0'] });
      },
      saveCustomRecipe: async ({
        recipe,
        forceSave = true,
        createCheckpoint = true,
        autoNavigate = true
      }) => {
        if (get().isReplay && !get().showOverrideRecipeDialog) {
          forceSave = false;
          set({ isReplay: false });
        }
        set((state) => ({
          ...state,
          currentRecipeDirtyState: recipe,
          builderSaving: true,
          showOverrideRecipeDialog: false
        }));

        if (!recipe?.id) {
          return;
        }

        if (!forceSave) {
          const shouldDeferToPrompt = await promptForRecipeOverride(recipe.id);
          if (shouldDeferToPrompt) {
            return set({ builderSaving: false });
          }
        }

        const cleanedLinks = cleanLinks(recipe?.links);
        // Apply the moderne-saas RecipeGraphBuilder algorithm client-side so
        // the persisted graph is in canonical form (Singletons collapsed to
        // a single vertex + REFERENCE leaves). Cross-fragment seams from
        // stitched fragment fetches are deduped here.
        const normalized = normalizeRecipeGraph(
          recipe?.nodes ?? [],
          cleanedLinks,
          ROOT_NODE_ID
        );
        const newRecipe: BuilderRecipe = {
          ...recipe,
          nodes: normalized.nodes,
          links: normalized.links
        };
        const nodeMap = new Map<number, GraphNodeData>(
          newRecipe?.nodes?.map((node) => [node.id, node]) || []
        );

        if (createCheckpoint) {
          setWithCheckpoint({
            currentRecipe: newRecipe,
            currentRecipeDirtyState: null,
            builderSaving: false,
            builderLoading: false,
            nodeMap
          });
        } else {
          set((state) => ({
            ...state,
            currentRecipe: newRecipe,
            currentRecipeDirtyState: null,
            builderSaving: false,
            builderLoading: false,
            nodeMap
          }));
        }
        // The db save is done after set to avoid flicker caused by db / state mismatch.
        // Run delete + put in a single Dexie rw transaction so an in-flight failure
        // never leaves the recipe absent from IndexedDB while currentRecipe has
        // already been swapped in memory.
        if (db?.builderRecipes) {
          await db.transaction('rw', db.builderRecipes, async () => {
            if (forceSave) {
              const isInDb = await db.builderRecipes.get(recipe.id);
              if (isInDb) {
                await db.builderRecipes.delete(recipe.id);
              }
            }
            await db.builderRecipes.put(newRecipe);
          });
        }
        await get().fetchCustomRecipes();

        if (autoNavigate && get().router) {
          await get().router.push('/builder');
        }
      },
      treeExpandAll: () => {
        set({
          treeExpandedItems: get().currentRecipe?.nodes.map((n) => String(n.id))
        });
      },
      treeCollapseAll: () => {
        set({ treeExpandedItems: [] });
      },
      updateCurrentRecipeDetails: withLoading(updateCurrentRecipeDetails),
      updateSelectedRecipeOptions: withLoading(updateSelectedNodeOptions),
      updateRootRecipe: async (nodes, links, selectedNodeId = ROOT_NODE_ID) => {
        const nodeMap = new Map<number, GraphNodeData>(
          nodes?.map((node) => [node.id, node]) || []
        );

        // Calculate selected recipe data before updating state
        // This allows us to batch all updates into a single set() call
        let selectedRecipeNode: GraphNodeData | undefined;
        let recipeCount = 0;
        let preconditionCount = 0;
        const preconditionChildNodeIds = new Set<number>();

        nodes?.forEach((node) => {
          if (node.id === selectedNodeId) {
            selectedRecipeNode = node;
          }

          if (node.isPrecondition) {
            preconditionCount += 1;
            for (const id of findDescendantNodeIds(node.id, links)) {
              preconditionChildNodeIds.add(id);
            }
          } else {
            if (preconditionChildNodeIds.has(node.id)) {
              preconditionCount += 1;
            } else {
              // exclude the root node from the list count
              if (ROOT_NODE_ID !== node.id) {
                recipeCount += 1;
              }
            }
          }
        });

        const descendantNodeIds =
          selectedRecipeNode?.id === ROOT_NODE_ID
            ? new Set<number>(nodes?.map((node) => node.id))
            : new Set<number>(findDescendantNodeIds(selectedNodeId, links));

        // Reuse currentRecipe object if nodes/links haven't changed (reference equality)
        // This prevents unnecessary re-renders in components that depend on currentRecipe
        const oldRecipe = get().currentRecipe;
        const newRecipe =
          oldRecipe?.nodes === nodes && oldRecipe?.links === links
            ? oldRecipe // Reuse existing object to prevent cascade
            : {
                ...oldRecipe,
                nodes,
                links
              };

        // Single batched state update to prevent cascading re-renders
        set((state) => ({
          ...state,
          currentRecipe: newRecipe,
          nodeMap,
          selectedRecipeNode,
          descendantNodeIds,
          recipeCount,
          preconditionCount
        }));
      },
      updateSelectedRecipe: (selectedRecipeNodeId) => {
        let selectedRecipeNode: GraphNodeData | undefined;
        let recipeCount = 0;
        let preconditionCount = 0;
        const preconditionChildNodeIds = new Set<number>();
        const currentRecipe = get().currentRecipe;
        const links = currentRecipe?.links;

        currentRecipe?.nodes.forEach((node) => {
          if (node.id === selectedRecipeNodeId) {
            selectedRecipeNode = node;
          }

          if (node.isPrecondition) {
            preconditionCount += 1;
            for (const id of findDescendantNodeIds(node.id, links)) {
              preconditionChildNodeIds.add(id);
            }
          } else {
            if (preconditionChildNodeIds.has(node.id)) {
              preconditionCount += 1;
            } else {
              // exclude the root node from the list count
              if (ROOT_NODE_ID !== node.id) {
                recipeCount += 1;
              }
            }
          }
        });

        // When a reference node is selected, highlight its target's subtree in
        // the 3D scene — references aren't rendered as orbs, so their own
        // (empty) descendant set would leave nothing lit up.
        const descendantLookupId =
          selectedRecipeNode?.isReference &&
          selectedRecipeNode.referencesNodeId !== undefined
            ? selectedRecipeNode.referencesNodeId
            : selectedRecipeNodeId;

        const descendantNodeIds =
          selectedRecipeNode?.id === ROOT_NODE_ID
            ? new Set<number>(currentRecipe?.nodes.map((node) => node.id))
            : new Set<number>(findDescendantNodeIds(descendantLookupId, links));

        set((state) => ({
          ...state,
          selectedRecipeNode,
          descendantNodeIds,
          recipeCount,
          preconditionCount
        }));
      },
      toggleNodePreconditionState: withLoading(async (nodeId: number) => {
        const { currentRecipe } = get();

        if (!currentRecipe) {
          return;
        }

        // Find the target node to determine its current precondition state
        const targetNode = currentRecipe.nodes.find(
          (node) => node.id === nodeId
        );

        if (!targetNode) {
          return;
        }

        // Get the new state (opposite of current state)
        const newPreconditionState = !targetNode.isPrecondition;

        // Get all descendants that need updating
        const nodesToUpdate = [
          nodeId,
          ...findDescendantNodeIds(nodeId, currentRecipe.links)
        ];

        // Update all affected nodes with the new precondition state
        const updatedNodes = currentRecipe.nodes.map((node) => {
          if (nodesToUpdate.includes(node.id)) {
            return {
              ...node,
              isPrecondition: newPreconditionState
            };
          }
          return node;
        });

        // Create updated recipe
        const updatedRecipe = {
          ...currentRecipe,
          nodes: updatedNodes
        };

        // Save the updated recipe
        await get().saveCustomRecipe({
          recipe: updatedRecipe,
          createCheckpoint: true
        });

        // Update the selected recipe to recalculate counts
        get().updateSelectedRecipe(
          get().selectedRecipeNode?.id || ROOT_NODE_ID
        );
      })
    };
  },
  ['currentRecipe']
);
